import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";
import { useLanguage } from "../../i18n/LanguageContext";
import { pluralForm } from "../../i18n/quotaCopy";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { CaseReportSections } from "../../pages/caseReportSections";
import CareDateField, { coerceCareDate } from "./CareDateField";

function pluralText(t, locale, count, key) {
  const template = pluralForm(locale, count, {
    one: t(`${key}.one`),
    few: t(`${key}.few`),
    many: t(`${key}.many`),
  });
  return template.replace("{n}", String(count));
}

const CATEGORIES = [
  "visit_note",
  "discharge_summary",
  "lab_result",
  "imaging_report",
  "prescription",
  "procedure",
  "immunization",
  "referral",
  "other",
];

export default function RecordDetail({
  open,
  recordId,
  subjectId,
  onOpenChange,
  onChanged,
  onLoaded,
}) {
  const { t, language } = useLanguage();
  const [record, setRecord] = useState(null);
  const [caseQuery, setCaseQuery] = useState(null);
  const [editing, setEditing] = useState(false);
  const [category, setCategory] = useState("other");
  const [title, setTitle] = useState("");
  const [facility, setFacility] = useState("");
  const [notes, setNotes] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [precision, setPrecision] = useState("day");
  const [confirming, setConfirming] = useState(false);
  const [alsoDocs, setAlsoDocs] = useState(false);
  const [busy, setBusy] = useState(false);
  const [overrideId, setOverrideId] = useState(null);
  const activeId = overrideId || recordId;
  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;

  useEffect(() => {
    setOverrideId(null);
  }, [recordId, open]);

  useEffect(() => {
    if (!open || !activeId || !subjectId) return;
    setEditing(false);
    setConfirming(false);
    setAlsoDocs(false);
    setCaseQuery(null);
    setRecord(null);
    let cancelled = false;
    axios
      .get(`${API}/subjects/${subjectId}/records/${activeId}`)
      .then(async (res) => {
        if (cancelled) return;
        const data = res.data?.data;
        setRecord(data);
        setCategory(data?.category || "other");
        setTitle(data?.title || "");
        setFacility(data?.facility || "");
        setNotes(data?.notes || "");
        setEventDate(data?.event_date || "");
        setPrecision(data?.event_date_precision || "day");
        onLoadedRef.current?.();
        if (data?.kind === "case" && data.query_id) {
          const detail = await axios.get(`${API}/council/history/${data.query_id}`);
          if (!cancelled) setCaseQuery(detail.data?.data || null);
        }
      })
      .catch((error) => {
        if (cancelled) return;
        const code = error.response?.data?.error?.code;
        toast.error(code === "file_unavailable" ? t("history.fileUnavailable") : t("history.loadError"));
        onLoadedRef.current?.();
      });
    return () => {
      cancelled = true;
    };
  }, [open, activeId, subjectId, t]);

  const openLinkedCase = () => {
    const caseLink = record?.case;
    const linked =
      record?.source === "case_attachment" && caseLink?.status === "linked" && subjectId;
    if (!linked) return;
    if (!caseLink.record_id) {
      toast.error(t("history.loadError"));
      return;
    }
    setOverrideId(caseLink.record_id);
  };

  const save = async () => {
    setBusy(true);
    try {
      const body = {
        category: record?.kind === "case" ? undefined : category,
        title,
        facility,
        notes,
      };
      const loadedDate = record?.event_date || "";
      const loadedPrecision = record?.event_date_precision || "day";
      if (!eventDate) {
        if (loadedDate) body.clear_event_date = true;
      } else if (eventDate !== loadedDate || precision !== loadedPrecision) {
        body.event_date = eventDate;
        body.event_date_precision = precision;
      }
      await axios.patch(`${API}/subjects/${subjectId}/records/${activeId}`, body);
      toast.success(t("history.saved"));
      setEditing(false);
      onChanged?.();
      onOpenChange(false);
    } catch {
      toast.error(t("history.saveError"));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      const params = alsoDocs ? { also_delete_documents: true } : {};
      await axios.delete(`${API}/subjects/${subjectId}/records/${activeId}`, { params });
      toast.success(t("history.deleted"));
      onChanged?.();
      onOpenChange(false);
    } catch {
      toast.error(t("history.saveError"));
    } finally {
      setBusy(false);
    }
  };

  const download = async (fileIndex = 0, name) => {
    const chosen = (record?.files || []).find((item) => item.index === fileIndex);
    try {
      const response = await axios.get(
        `${API}/subjects/${subjectId}/records/${activeId}/file`,
        { params: { file_index: fileIndex }, responseType: "blob" },
      );
      const blob = new Blob([response.data], {
        type: chosen?.content_type || record?.file?.content_type || "application/octet-stream",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = name || chosen?.filename || record?.filename || "record";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      const code = error.response?.data?.error?.code;
      toast.error(code === "file_unavailable" ? t("history.fileUnavailable") : t("history.downloadError"));
    }
  };

  const isCase = record?.kind === "case";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="record-detail">
        <DialogHeader>
          <DialogTitle>{record?.title || t("history.timeline")}</DialogTitle>
          <DialogDescription className="sr-only">{t("history.timeline")}</DialogDescription>
        </DialogHeader>
        {!record ? (
          <p className="text-sm text-muted-foreground" data-testid="record-detail-loading">
            {t("common.loading")}
          </p>
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              {record.event_date || t("history.dateUnknown")}
              {" · "}
              {t(`history.category.${record.category}`)}
              {" · "}
              {t(`history.source.${record.source}`)}
            </p>
            {!editing && record.event_date_origin === "case_default" ? (
              <button
                type="button"
                className="block text-left text-xs text-muted-foreground underline underline-offset-2"
                onClick={() => setEditing(true)}
                data-testid="history-date-origin-hint"
              >
                {t("history.dateFromCase")}
              </button>
            ) : null}
            {!editing && record.source === "case_attachment" && record.case?.status === "linked" ? (
              <button
                type="button"
                className="block text-left text-sm underline underline-offset-2"
                onClick={openLinkedCase}
                disabled={busy}
                data-testid="history-from-case"
              >
                {t("history.fromCase").replace("{title}", record.case.title || "")}
              </button>
            ) : null}
            {!editing && record.source === "case_attachment" && record.case?.status === "pending" ? (
              <p className="text-sm text-muted-foreground" data-testid="history-from-case">
                {t("history.fromCasePending")}
              </p>
            ) : null}
            {!editing && record.source === "case_attachment" && record.case?.status === "deleted" ? (
              <p className="text-sm text-muted-foreground" data-testid="history-from-case">
                {t("history.fromCaseDeleted")}
              </p>
            ) : null}
            {!editing && record.facility ? <p className="text-sm">{record.facility}</p> : null}
            {!editing && record.notes ? <p className="text-sm whitespace-pre-wrap">{record.notes}</p> : null}
            {!editing && record.kind === "document" && (
              record.preview_available === false ? (
                <p className="text-sm text-muted-foreground">{t("history.previewUnavailable")}</p>
              ) : (
                record.extracted_text ? (
                  <pre className="text-xs whitespace-pre-wrap bg-muted/40 rounded-md p-3 max-h-48 overflow-auto">
                    {record.extracted_text}
                  </pre>
                ) : null
              )
            )}
            {isCase && caseQuery && <CaseReportSections t={t} query={caseQuery} />}
            {editing && (
              <div className="space-y-3">
                {!isCase && (
                  <div className="space-y-1.5">
                    <Label>{t("history.category")}</Label>
                    <select
                      className="h-9 w-full rounded-md border border-border bg-background px-2 text-sm"
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                    >
                      {CATEGORIES.map((key) => (
                        <option key={key} value={key}>
                          {t(`history.category.${key}`)}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label>{t("history.titleField")}</Label>
                  <Input value={title} onChange={(e) => setTitle(e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label>{t("history.facility")}</Label>
                  <Input value={facility} onChange={(e) => setFacility(e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label>{t("history.notes")}</Label>
                  <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>{t("history.datePrecision")}</Label>
                    <select
                      className="h-9 w-full rounded-md border border-border bg-background px-2 text-sm"
                      value={precision}
                      onChange={(e) => {
                        const next = e.target.value;
                        setPrecision(next);
                        setEventDate((current) => coerceCareDate(current, next));
                      }}
                    >
                      {["day", "month", "year"].map((key) => (
                        <option key={key} value={key}>
                          {t(`history.precision.${key}`)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>{t("history.eventDate")}</Label>
                    <CareDateField precision={precision} value={eventDate} onChange={setEventDate} />
                  </div>
                </div>
              </div>
            )}
            {confirming && (
              <div className="space-y-2 rounded-md border border-border p-3">
                <p className="text-sm">{t("history.deleteConfirm")}</p>
                {isCase && Number(record.attachment_count) > 0 && (
                  <label className="flex items-start gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={alsoDocs}
                      onChange={(e) => setAlsoDocs(e.target.checked)}
                      data-testid="delete-case-documents"
                    />
                    <span>
                      {pluralText(
                        t,
                        language,
                        Number(record.attachment_count),
                        "history.deleteCaseDocuments",
                      )}
                    </span>
                  </label>
                )}
                <div className="flex gap-2">
                  <Button variant="destructive" size="sm" onClick={remove} disabled={busy}>
                    {t("history.delete")}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setConfirming(false)}>
                    {t("history.cancel")}
                  </Button>
                </div>
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              {(record.files?.length
                ? record.files
                : record.file
                  ? [{ index: 0, filename: record.filename }]
                  : []
              ).map((item) => (
                <Button
                  key={item.index}
                  variant="outline"
                  size="sm"
                  onClick={() => download(item.index, item.filename)}
                >
                  {item.filename || t("history.download")}
                </Button>
              ))}
              {!editing ? (
                <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
                  {t("history.edit")}
                </Button>
              ) : (
                <Button size="sm" onClick={save} disabled={busy}>
                  {t("history.save")}
                </Button>
              )}
              {!confirming && (
                <Button variant="ghost" size="sm" onClick={() => setConfirming(true)}>
                  {t("history.delete")}
                </Button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
